import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import '../styling/emergency.css';

const Emergency = ({ handleEmergencySubmit, setStep }) => {
  const [selectedEmergencies, setSelectedEmergencies] = useState([]);
  const [showTreatmentOptions, setShowTreatmentOptions] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const emergencyTypes = {
    "Respiratory": [
      "Laryngospasm",
      "Bronchospasm",
      "Aspiration",
      "OMS difficult airway algorithm",
      "Hyperventilation Syndrome",
      "Unanticipated Endotracheal Intubation",
      "Hypoxia",
      "Loss of Airway"
    ],
    "Cardiovascular": [
      "Angina/Acute Myocardial Infarction",
      "Hypotension",
      "Hypertension"
    ],
    "Neurological": [
      "Seizure",
      "Syncope",
      "Delayed Emergence",
      "Intraoperative Awareness"
    ],
    "Intravenous": [
      "Intra-arterial injection",
      "Neurocardiogenic (Vasovagal) Syncope",
      "Pseudoaneurysm"
    ],
    "Immune Reaction": [
      "Allergic Reaction"
    ],
    "Hyperthermia": [
      "Malignant Hyperthermia"
    ],
    "Emergency Treatment": [
      "Acute Phase Stabilization"
    ],
  };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setSelectedEmergencies(prevState =>
      checked
        ? [...prevState, value]
        : prevState.filter(emergency => emergency !== value)
    );
  };

  const handleSubmit = () => {
    handleEmergencySubmit({
      emergencies: selectedEmergencies,
    });
    setShowTreatmentOptions(true);
  };

  const handleDone = () => {
    setStep('mainScreen');
  };

  const treatmentOptions = {
    "Laryngospasm": `
      • Administer 100% oxygen via nasal mask
      • Suction the oropharynx, hypopharynx, and nasopharynx with a tonsil suction tip
      • Suction/remove all blood, saliva, and foreign material from the oral cavity
      • Pack the surgical site to prevent bleeding into the hypopharynx
      • Draw the tongue and/or mandible forward
      • Depress the patient’s chest while listening with the ear close to the patient’s mouth for a rush of air. If a clear “huff” of air is heard, the airway is patent, and the spasm probably is resolved.
      • If a clear “huff” is not heard:
        – Try to break the spasm mechanically by attempting to ventilate the patient with a full face mask and 100% oxygen
        – Deepen the anesthetic with 0.5 Mg/kg propofol before administering muscle relaxant
        – Administer an iv dose of succinylcholine (partial spasm: 10-20 mg iv; complete spasm: 20-40 mg) immediately and oxygen under pressure (alternative: rocuronium 10-15 mg iv)
        – Administer intubating iv dose of succinylcholine, intubate, and secure the airway (alternative: rocuronium 0.6 Mg/kg iv)
        – Consider atropine with repeat dose of succinylcholine for pediatric patients to prevent bradycardia`,

    "Bronchospasm": `
      **Awake patient**
      • Administer 4-8 puffs of a beta-agonist inhalant either via inhaler or nebulizer in an awake patient (2-4 puffs for pediatric patient)
      • Administer supplemental oxygen via face mask
      
      **Obtunded nonintubated patient**
      • Consider albuterol nebulizer via facemask prior to administration of epinephrine
      • Administer 0.3 To 0.5 Mg epinephrine (1:1,000 solution) subcutaneously
      • If hypotension is present and believed to be a sign of acute anaphylaxis
      • Administer intravenous bolus dose of 10 to 20 mcg of a 1:10,000 solution of epinephrine titrated to response or
      • Administer 1mg of epinephrine in 500 ml of d5w (2mcg/ml) with mini-drip at rate of 2-10 mcg/min
      
      **Intubated patient**
      • Administer 5 to 10 puffs of a beta-agonist inhalant from an inhaler into the open end of the endotracheal tube or through a medication elbow added to the circuit designed for inhalant medication delivery
      • Consider deepening plane of anesthetic
      • Check breathing circuit
      • If persistent, consider epinephrine as above for nonintubated patient
      • Administer 100% oxygen with positive pressure ventilation
      
      **Hypoxemia in spontaneously breathing patient**
      • Administer 100% Oxygen By Face Mask
      • Consider Reversal Of Sedative Medications
      • Perform Chest Auscultation And Treat Findings
      • Intubate And Administer Positive-Pressure Ventilation With 100% Oxygen If The Patient Continues To Deteriorate With Hypoxemia, Impending Respiratory Muscle Exhaustion, Or Worsening Obtundation`,

    "Aspiration": `
      • Place the patient in the trendelenburg position with the head down at least 15 degrees and rolled onto the right side
      • Clear any vomitus in the oropharynx with finger sweeps and large-bore suction
      • If no improvement, place the patient in the supine position and intubate (may be necessary to use muscle relaxants prior
      to intubation)
      • Remove any solid particles in the laryngeal region with magill forceps at time of intubation
      • Oxygenate the patient as soon as possible and manage any bronchospasm as previously described
      • Tracheobronchial lavage is not performed except for small volumes (5 to 10 ml) of saline to facilitate suction
      • Maintain a patent airway and continue ventilation with 100% oxygen in route to hospital`,

    "OMS difficult airway algorithm": `
      • Chin lift/jaw thrust
      • Pull tongue forward, reposition airway
      • Full face mask positive pressure ventilation
      • Consider nasal/oral pharyngeal airways
      • Consider a supraglottic airway device or other adjunct airway devices
      • Consider endotracheal intubation
      • Consider needle cricothyrotomy
      • Consider surgical cricothyrotomy
      • Consider tracheostomy`,

    "Hyperventilation Syndrome": `
      • Hyperventilation Syndrome treatment options`,

    "Unanticipated Endotracheal Intubation": `
      • Unanticipated Endotracheal Intubation treatment options`,

    "Hypoxia": `
      • Hypoxia treatment options`,

    "Loss of Airway": `
      • Loss of Airway treatment options`,

    "Angina/Acute Myocardial Infarction":`
      • Terminate the surgery and all stimulation of the patient
      • Place the patient in a comfortable position and loosen tight clothing
      • Administer oxygen at 4 l/min via mask or nasal cannula
      • Apply pulse oximeter and ecg monitor
      • Obtain vital signs with automatic or standard blood pressure cuff
      • Administer nitroglycerin sublingually or via spray every five minutes if the systolic blood pressure is greater than 90 mm/hg.
      • Establish iv access, if not already obtained
      • If ami is suspected, activate the ems 911 system.
      • Employ the pneumonic mona, but not in that order; use onam because use of chewed aspirin may decrease absorption of
      subsequent nitroglycerin
      • Administer nonenteric aspirin (160-325 mg) and direct the patient to chew one and swallow one, except in cases of allergy
      to this drug
      • Administer morphine, 1 to 3 mg iv every 5 minutes to control pain if systolic blood pressure is greater then 90 mm hg.
      Watch carefully so the patient does not become overly sedated or lose respiratory drive.
      Monitor vital signs closely. Watch for hypotension, hypoxemia, bradycardia, ventricular dysrhythmias, and cardiac arrest and
      manage appropriately per ACLS protocols.`,

    "Hypotension": `
      • Treatment of hypotension is directed toward its cause
      • Decrease anesthetic depth if patient is under anesthesia
      • Terminate the surgery
      • Place the patient in the supine position with legs elevated
      • Administer 100% oxygen
      • Re-evaluate blood pressure, heart rate, and rhythm and treat dysrhythmias
      • Administer a bolus of isotonic fluid
      • Titrate ephedrine or phenylephrine or other suitable vasopressor to preserve adequate systemic pressures (ephedrine is
      generally used unless there is tachycardia and hypotension in which case phenylephrine may be a better choice.)
      **Dosing of Sympathomimetic Agents for Treatment of Hypotensive Emergencies**
      • Ephedrine: Dilute a 50-mg vial is diluted in 9 mL of saline to make a 5-mg/mL solution and administer at a dose of 2.5 to 5
      mg IV, which can be repeated until the blood pressure is stabilized. The drug’s effects occur in approximately 10 minutes,
      peak at 20 minutes, and last approximately 4 hours.
      • Phenylephrine: Dilute single-dose vial (10 mg/mL) in 10 mL of saline; discard 9 mL and dilute with an additional 9 mL of
      saline to create a 0.1-mg/mL concentration; administer IV in 0.1mg/mL increments until the desired effects are achieved. The
      effects are seen within 2 to 3 minutes and last for approximately 15 minutes.
      • Epinephrine: (may not be the drug of choice in the treatment of hypotensive episodes related to anesthesia); Dilute 1 mg in
      500 mL of 5% dextrose (2 mcg/mL); administer as mini drip 2-10 mcg/min to clinical end point.`,

    "Hypertension": `
      Therapeutic approaches to treating perioperative hypertension are directed at producing vasodilatation or altering cardiac
      output by beta-adrenergic receptor blockers. Among the drugs that may be useful are beta blockers such as esmolol (Brevibloc)
      and metoprolol (Lopressor), the selective alpha blocker labetalol (Normodyne, Trandate), and the vasodilator hydralazine.
      **Esmolol dosing**
      immediate control dosing:
      • 80 mg (approximately 1 mg/kg) over 30 seconds
      • Followed with a 150 mcg/kg/min infusion (0.15 mg/kg/min) that is adjusted as required to a maximum of 300 mcg/kg/min
      (0.30 mg/kg/min) to maintain the desired heart rate and/or blood pressure.
      Gradual control dosing (when there is time to titrate):
      • Loading dose infusion of 500 mcg/kg/min for 1 minute
      • Followed with a 50 mcg/kg/min over 4-minutes
      If an adequate therapeutic effect is not seen in 5 minutes:
      • Loading dose infusion of 500 mcg/kg/min for 1 minute
      • Followed with a 100 mcg/kg/min
      **Metoprolol dosing**
      • Initial dose 5 mg IV over 2 minutes
      **Labetalol dosing (a selective alpha 1 blocker, and non selective beta blocker)**
      • Initial adult dose 5 to 20 mg IV over 2 minutes
      • Followed by 2 mg/min (maximum dose, 300 mg) IV infusion
      • Onset 5 minutes, duration 3-6 hours
      **Hydralazine dosing (a direct vasodilator for patient who can tolerate an increase in heart rate and cardiac output but who has
      a history of asthma)**
      • Initial adult dose of 5mg, can titrate up to 25mg
      • Onset 5 minutes, duration 2 hours`,

    "Seizure": `
      • Prevent injury to the uncontrolled unconscious patient
      • Loosen clothing about the neck
      • Place pillow under the head
      • Place padded tongue blade between the teeth or a side-action mouth prop if evidence that the patient’s tongue is being
      traumatized
      • Consider checking blood sugar
      **If a local anesthetic overdose is suspected:**
      • Place patient in supine position
      • Administer oxygen
      • Maintain airway
      • Transport to hospital for consideration of lipid rescue
      **If seizures continue unabated following initial treatment:**
      • Activate ems system
      • Administer iv diazepam 5 mg over one minute titrated to effect or; iv midazolam 2.5 Mg over one minute titrated to effect`,

    "Syncope": `
      • Syncope treatment options`,

    "Delayed Emergence": `
      • Delayed Emergence treatment options`,

    "Intraoperative Awareness": `
      • Intraoperative Awareness treatment options`,

    "Intra-arterial injection": `
      • Prevention is the best treatment
      • No single treatment regimen has been found to be completely effective
      • Leave the needle in place
      • Inject 10 ml of 1% plain lidocaine into the artery
      • Consider transfer patient to a hospital for further therapy and consider vascular consult`,

    "Neurocardiogenic (Vasovagal) Syncope": `
      • Place Patient In A Reclining Position With The Legs Elevated
      • Maintain Airway
      • Deliver 100% Oxygen
      • Support Respiration If Needed
      • Take Vital Signs
      • Apply Cool Compress To Forehead
      • Administer Spirits Of Ammonia, If Necessary
      • Administer Iv Atropine 0.5 Mg Every 3-5 Minutes Up To 3 Mg In The Presence Of Bradycardia`,

    "Pseudoaneurysm": `
      • Pseudoaneurysm treatment options`,

    "Allergic Reaction": `
      Mild reactions usually manifest as skin reaction without other systemic signs or symptoms
      • Identify causative agent, if time permits
      • Remove, dilute, negate, or antagonize, if possible
      • Administer diphenhydramine adult dose 25-50 mg po, 10-50 mg iv at a rate generally not exceeding 25 mg/min, deep
      intramuscular injection of 100 mg if required (maximum daily dosage is 400 mg
      OR
      Chlorpheniramine maleate adult dose 5-20 mg, iv, im, or sq injection as a single dose; tablets or syrup, 4 mg every 4-6 hours
      Severe Reactions (Anaphylaxis) manifest with all signs and symptoms of allergic reaction with skin rash, watery eyes and
      nose, abdominal cramps, wheezing, tachycardia, and hypotension
      It is important to have a plan or algorithm to guide treatment of a severe allergic reaction and impending cardiovascular
      collapse.
      1. Stop administration of the antigen
      2. Follow the ABCs:
      • Maintain an airway with supplemental oxygen
      • Support respiration and breathing
      • Support circulation by providing an IV route for volume loading with crystalloid solution. In the adult patient, rapidly
      infuse 1 L lactated Ringer’s solution. For children, administer fluid boluses of 20 mL/kg of lactated Ringer’s solution or
      normal saline.
      3. Administer epinephrine
      • Intravenous
      – Use a premixed solution of 1:10,000 (1 mg of epinephrine in 10 mL)
      – Adults: Titrate 0.2 mg (2 mL) to 0.5 mg (5 mL) to effect and repeat every 2 to 5 minutes as needed
      – Children: 0.01 mg/kg
      • Intramuscular or Subcutaneous
      – If an IV route is unavailable, the drug can be administered IM or subcutaneously using a 1:1,000 solution (1 mg/mL).
      – Adults: 0.3 to 0.5 mg of a 1:1,000 concentration. This dose may be repeated at 10- to 20-minute intervals based on
      patient response.
      – Children: 0.01 mg/kg. This dose may be repeated at 10- to 20-minute intervals based on patient response.
      4. As epinephrine administration continues, an H1-antihistamine may be administered IM or IV. Diphenhydramine (Benadryl)
      can be given IV in a dose of 50 mg for adults and older children. The child 6 to 12 years of age receives 25 mg. An H2 blocker,
      such as famotidine (Pepcid), can be administered to adults IV starting at 20 mg. The dose for children older than 1 year is 0.5
      mg/kg IV.
      5. Albuterol inhaler can be administered at 4 to 5 puffs as needed (2 to 4 puffs for pediatric patient.
      6. Administer corticosteroids
      The use of corticosteroids has been advocated in the past to treat asthma, edema, or intense pruritus. Although these drugs
      act too slowly to be life-saving in an acute anaphylactic episode, they are important in combating immunologic reactions
      and regaining homeostasis. Because of their slow action, they are the last drugs administered in the therapeutic regimen.
      Options include the following:
      • Hydrocortisone sodium succinate (Solu-Cortef) at a dose of 100 mg for 1 minute
      • Dexamethasone (Decadron) at a dose of 4 to 12 mg given slowly IV or IM
      Doses can be repeated at intervals as determined by the severity of symptoms. Because anaphylaxis is an acute process,
      corticosteroid use normally can be stopped after 36 hours without adverse sequelae.
      **Follow-up Measures**
      Pharyngeal edema and upper airway obstruction can occur and persist after the administration of epinephrine. Endotracheal
      intubation or tracheotomy may be necessary to secure an airway. If shock continues, the patient should be placed in a slight
      Trendelenburg position to provide circulatory support. If the patient develops dyspnea and wheezing, a semireclining position
      may be more helpful. If a significant decrease in blood pressure persists despite appropriate volume expansion and the use of
      epinephrine, an adjunctive vasopressor should be considered. (See the section on treatment of hypotension for management
      of this problem.)
      Seizures may occur during allergic reactions and usually are due to circulatory and/or respiratory inadequacy. If convulsions
      persist after corrective measures have been taken to ensure cardiorespiratory sufficiency, diazepam, 1 mL (5 mg), can be
      administered IV for 1 minute, with midazolam used as an alternative. It is important to wait 2 minutes for the effect before
      giving another dose.
      After successful treatment of a severe allergic reaction, the patient should be hospitalized and observed for 24 hours, receiving
      prophylactic antihistamines. Anaphylactic reactions have been known to recur after the initial effects of the counteracting
      drugs have worn off.`,

    "Malignant Hyperthermia": `
      Following are the primary signs of impending MH:
      • Increase in end-tidal carbon dioxide (doubling or tripling, may occur rapidly or during 10 to 20 minutes)
      • Unexplained tachycardia, tachypnea, hypercarbia
      • Generalized muscle rigidity
      • Masseter muscle rigidity
      • Hyperthermia (often a late sign)
      • Respiratory and/or metabolic acidosis
      • Sudden/unexpected cardiac arrest (consider as secondary to hyperkalemia and treat as such)
      Contact telephone number: 800-MHHYPER (800-644-9737)`,

    "Acute Phase Stabilization": `
      Immediately activate the EMS for patient transport and notify the receiving hospital to prepare for continuing treatment of an
      MH patient.
      • Immediately discontinue use of all volatile inhalation anesthetics and succinylcholine. Hyperventilate with 100% oxygen at
      high gas flows, at least 10 L/min. The circle system and carbon dioxide absorbent need not to be changed.
      • Solubilize the dantrolene according to manufacturer’s instructions. If using the older formulation, (Dantrolene), it takes
      time to mix the solution, so it is recommended that 1 person be designated for this task.
      • Administer dantrolene, 2 to 3 mg/kg initial bolus rapidly, with increments up to a total of 10 mg/kg. Continue to administer
      dantrolene until signs of MH (eg, tachycardia, rigidity, increased end-tidal carbon dioxide, and temperature elevation) are
      controlled. Occasionally, a total dose greater than 10 mg/kg may be needed.
      Administer bicarbonate to correct metabolic acidosis, as guided by blood gas analysis. In the absence of blood gas analysis,
      administer 1 to 2 mEq/kg.
      • Simultaneously with the previous steps, actively cool the hyperthermic patient with IV cold saline (not Ringer’s lactate) 15
      mL/kg every 15 minutes 3 times.
      – Lavage stomach, bladder, rectum, and open cavities with cold saline as appropriate. May not be appropriate for OMS
      patients.
      – Surface cool with ice and hypothermia blanket. A supply of ice or an ice machine is helpful for this purpose.
      – Monitor closely because excessively vigorous treatment may lead to hypothermia.
      • Arrhythmias usually respond to treatment of acidosis and hyperkalemia. If they persist or are life threatening, use standard
      antiarrhythmic agents, except calcium channel blockers, which may cause hyperkalemia and cardiovascular collapse.
      • Determine and monitor end-tidal carbon dioxide concentrations; arterial, central, or femoral venous blood gases; serum
      potassium and calcium levels; clotting studies; and urine output.
      • Treat hyperkalemia with hyperventilation, bicarbonate, and IV glucose and insulin (10 U of regular insulin in 50 mL of 50%
      glucose titrated to potassium level or 0.15 U/kg of regular insulin in 1 mL/kg of 50% glucose). Life-threatening hyperkalemia
      also may be treated with calcium (eg, 2-5 mg/kg of calcium chloride).
      • Ensure urine output of greater than 2 mL/kg/h by hydration and/or administration of furosemide and additional mannitol,
      if necessary. Consider central venous or direct arterial monitoring because of potential fluid shifts and hemodynamic
      instability.
      • Sudden unexpected cardiac arrest in children: Children younger than 10 years who experience sudden cardiac arrest after
      succinylcholine administration in the absence of hypoxemia and anesthetic overdose should be treated initially for acute
      hyperkalemia. In this situation, calcium chloride should be administered with other means to reduce serum potassium
      levels. Affected children should be presumed to have subclinical muscular dystrophy, and a neurologist should be
      consulted. `
  };

  return (
    <div className="emergency-check">
      <h1>Emergency Action Required</h1>
      <a href="tel:911">Call 911</a>
      
      <p>Select the type(s) of emergency:</p>
      <div className="emergency-checkbox-container">
        {Object.entries(emergencyTypes).map(([category, emergencies]) => (
          <div key={category} className="emergency-category">
            <h3>{category}</h3>
            {emergencies.map((emergency, index) => (
              <div key={`${category}-${index}`} className="emergency-checkbox">
                <input
                  type="checkbox"
                  id={`emergency-${category}-${index}`}
                  value={emergency}
                  checked={selectedEmergencies.includes(emergency)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor={`emergency-${category}-${index}`}>{emergency}</label>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="emergency-button-container">
        <button className="emergency-submit-btn" onClick={handleSubmit}>Submit</button>
      </div>
      {showTreatmentOptions && (
        <div className="treatment-options">
          <h3>Treatment Options</h3>
          {selectedEmergencies.map(emergency => (
            <div key={emergency} className="treatment-option">
              <h4>{emergency}</h4>
              <p>{treatmentOptions[emergency]}</p>
            </div>
          ))}
          <div className="done-button-container">
            <button className="emergency-submit-btn" onClick={handleDone}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emergency;
